<template>
  <div class="login-background">
    <div class="login-main-block">
      <div class="login-news">
        <a :href="manualURL" target="_blank">
          <img :src="ushowBanner" alt="Banner" />
        </a>
      </div>
      <div class="login-interface">
        <div class="login-top-and-middle">
          <div class="login-top">
            <div class="login-img">
              <img class="logo-img" :src="ushowLogo" alt="Ushow" />
            </div>
            <div v-if="isLogin" class="login-img">
              <img class="slogan-img" :src="ushowSlogan" alt="Slogan" />
            </div>
            <div v-else>
              <div v-if="step === 1" class="step-info">
                <div class="main-text">忘記密碼</div>
                <div class="sub-text">請輸入註冊時的帳號</div>
              </div>
              <div v-else-if="step === 2" class="step-info">
                <div class="main-text">信箱驗證</div>
                <div class="sub-text">請輸入傳送至信箱的驗證碼</div>
                <!-- <div class="res-email">{{ resEmail }}</div> -->
              </div>
              <div v-else-if="step === 3" class="step-info">
                <div class="main-text">設定新密碼</div>
              </div>
            </div>
          </div>
          <form v-if="isLogin" class="login-middle" @submit.prevent="login">
            <div class="account-title">帳號</div>
            <div class="login-input">
              <input
                class="account-input"
                type="text"
                v-model="account"
                required
              />
            </div>
            <div class="password-title">密碼</div>
            <div class="login-input password-input-wrapper">
              <input
                class="password-input"
                :type="isPasswordVisible ? 'text' : 'password'"
                v-model="password"
                required
              />
              <button
                type="button"
                class="password-toggle-button"
                :aria-label="isPasswordVisible ? '隱藏密碼' : '顯示密碼'"
                @click="isPasswordVisible = !isPasswordVisible"
              >
                <img
                  :src="isPasswordVisible ? hidePasswordIcon : showPasswordIcon"
                  :alt="isPasswordVisible ? '隱藏密碼' : '顯示密碼'"
                />
              </button>
            </div>
            <div class="login-button">
              <button type="submit">&#30331;&#20837;</button>
            </div>
            <div class="flex-end-center w-100 mt-12px">
              <div class="forget-password" @click="forgetPassword">
                忘記密碼
              </div>
            </div>
          </form>
          <form
            v-else-if="!isLogin && step === 1"
            class="login-middle"
            @submit.prevent="retrieveAccount"
          >
            <div class="account-title">帳號</div>
            <div class="login-input">
              <input
                class="account-input"
                type="text"
                v-model="account"
                required
                :disabled="isVerifyLocked"
              />
            </div>
            <div class="login-button">
              <button type="submit" :disabled="isVerifyLocked">下一步</button>
            </div>
            <div v-if="isVerifyLocked" class="lock-until-counter mt-5px">
              <div class="verify-lock-message">
                {{ "請等待 " + lockCountdownText + " 後重試" }}
              </div>
            </div>
            <div class="back-to-login" @click="backToLogin">返回登入</div>
          </form>
          <form
            v-else-if="!isLogin && step === 2"
            class="login-middle"
            @submit.prevent="verifyCode"
          >
            <div class="verify-inputs">
              <input
                v-for="(code, index) in verifyCodes"
                :key="index"
                :ref="`verifyInput${index}`"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="1"
                class="verify-input"
                :class="`code-${index + 1}`"
                :value="code"
                :disabled="isVerifyLocked"
                @input="handleVerifyInput(index, $event)"
                @keydown="handleVerifyKeydown(index, $event)"
                @paste="handleVerifyPaste(index, $event)"
              />
            </div>
            <div class="login-button">
              <button type="submit" :disabled="isVerifyLocked || isLockEnter">
                驗證
              </button>
            </div>
            <div v-if="isVerifyLocked" class="lock-until-counter mt-5px">
              <div class="verify-lock-message">
                {{ "請等待 " + lockCountdownText + " 後重試" }}
              </div>
            </div>
            <div v-else class="resend-verify-code">
              <div class="confirm-message">沒有收到驗證碼？</div>
              <div class="resend-block">
                <div v-if="resended && !isVerifyLocked" class="resend-counter">
                  {{ counter >= 10 ? "00:" + counter : "00:0" + counter }}
                </div>
                <div v-else class="resend-request" @click="retrieveAccount">
                  重新寄送
                </div>
              </div>
            </div>
          </form>
          <form
            v-else-if="!isLogin && step === 3"
            class="login-middle"
            @submit.prevent="resetPassword"
          >
            <div class="account-title">輸入新密碼</div>
            <div class="login-input">
              <input
                class="account-input"
                type="password"
                v-model="newPassword"
                required
              />
            </div>
            <div class="password-title">確認新密碼</div>
            <div class="login-input">
              <input
                class="password-input"
                type="password"
                v-model="confirmNewPassword"
                required
              />
            </div>
            <div class="login-button">
              <button type="submit">確定</button>
            </div>
          </form>
        </div>
        <div class="login-bottom">
          <div class="copyright">
            Copyright &copy; 2025 百揚資訊科技有限公司
          </div>
          <div class="version">{{ version }}</div>
        </div>
      </div>
    </div>
    <itemModal
      id="forcePasswordModal"
      modalTitle="修改密碼"
      modalClass="modal-sm"
      modalBodyClass="px-0"
      :trigger="forcePasswordModalTrigger"
      @onSaved="saveExpiredPassword"
      @onClosed="cancelExpiredPasswordChange"
    >
      <template #modal-body>
        <div class="inputs-group">
          <inputTextPart class="w-100" title="舊密碼" :isRequired="true">
            <input
              type="password"
              v-model="expiredPassword.OldPassword"
              required
            />
          </inputTextPart>
          <inputTextPart class="w-100" title="新密碼" :isRequired="true">
            <input
              type="password"
              v-model="expiredPassword.NewPassword"
              required
            />
          </inputTextPart>
          <inputTextPart class="w-100" title="確認新密碼" :isRequired="true">
            <input
              type="password"
              v-model="expiredPassword.ConfirmPassword"
              required
            />
          </inputTextPart>
        </div>
      </template>
    </itemModal>
  </div>
</template>

<script>
import { useLoadingStore } from "@/stores/loadingStore";
import itemModal from "@/components/modals/ItemModal.vue";
import inputTextPart from "@/components/parts/inputs/InputTextPart.vue";
export default {
  components: {
    itemModal,
    inputTextPart,
  },
  data() {
    return {
      account: "",
      password: "",
      isPasswordVisible: false,
      version: require("@/../package.json").version,
      ushowBanner: require("@/assets/images/Login/Ushow_banner.png"),
      ushowLogo: require("@/assets/images/Login/Ushow_logo.svg"),
      ushowSlogan: require("@/assets/images/Login/Ushow_slogan.svg"),
      showPasswordIcon: require("@/assets/images/Tools/Show.svg"),
      hidePasswordIcon: require("@/assets/images/Tools/Hide.svg"),
      manualURL: "https://help.ushowpos.com/",
      isLogin: true,
      step: 1,
      resEmail: "",
      counter: 30,
      lockUntil: null,
      lockCounter: 0,
      lockInterval: null,
      verifyCodes: Array(6).fill(""),
      newPassword: "",
      confirmNewPassword: "",
      isLockEnter: false,
      forcePasswordModalTrigger: false,
      isForcedPasswordChangeActive: false,
      expiredPassword: {
        OldPassword: "",
        NewPassword: "",
        ConfirmPassword: "",
      },
    };
  },
  computed: {
    completeVerifyCode() {
      return this.verifyCodes.join("");
    },
    resended() {
      return this.counter > 0;
    },
    isVerifyLocked() {
      return this.lockCounter > 0;
    },
    lockCountdownText() {
      const minutes = Math.floor(this.lockCounter / 60);
      const seconds = this.lockCounter % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0"
      )}`;
    },
  },
  methods: {
    backToLogin() {
      this.step = 1;
      this.isLogin = true;
    },
    resetPassword() {
      if (this.newPassword !== this.confirmNewPassword) {
        this.$func.message({
          type: "alert",
          mainMessage:
            "\u5169\u6b21\u8f38\u5165\u7684\u5bc6\u78bc\u4e0d\u4e00\u81f4",
        });
        return;
      }
      this.$api.users
        .forgetPasswordResetPassword({
          Account: this.account,
          Password: this.$func.crypto.encrypt(this.newPassword),
          ConfirmPassword: this.$func.crypto.encrypt(this.confirmNewPassword),
        })
        .then(() => {
          this.$func.message({
            type: "success",
            mainMessage: "\u5bc6\u78bc\u5df2\u91cd\u8a2d\u6210\u529f",
          });
          this.backToLogin();
        });
    },
    verifyCode() {
      if (this.isVerifyLocked) return;
      this.$api.users
        .forgetPasswordVerifyCode({
          Account: this.account,
          VerifyCode: this.completeVerifyCode,
        })
        .then((data) => {
          const lockUntil = data?.LockUntil;
          if (!data.IsSuccess) {
            this.isLockEnter = data.IsLockEnter;
            this.$func.message({
              type: "error",
              mainMessage: data.Message,
            });
            if (lockUntil) {
              this.startVerifyLock(lockUntil);
            }
          } else {
            if (data.IsSuccess) {
              this.$func.message({
                type: "success",
                mainMessage: data.Message,
              });
              this.step = 3;
            }
          }
        })
        .catch((error) => {
          const lockUntil = error.data?.LockUntil;
          if (!error.data.IsSuccess && lockUntil) {
            this.startVerifyLock(lockUntil);
          }
          this.$func.message({
            type: "error",
            mainMessage: error.data?.Message || error,
          });
        });
    },
    focusVerifyInput(index) {
      this.$nextTick(() => {
        const target = this.$refs[`verifyInput${index}`];
        const input = Array.isArray(target) ? target[0] : target;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    setVerifyCodeValue(index, value) {
      this.verifyCodes.splice(index, 1, value);
    },
    handleVerifyInput(index, event) {
      if (this.isVerifyLocked) return;
      const rawValue = event.target.value || "";
      const digits = rawValue.replace(/\D/g, "");

      if (!digits) {
        this.setVerifyCodeValue(index, "");
        return;
      }

      const values = digits.slice(0, this.verifyCodes.length - index).split("");
      values.forEach((value, offset) => {
        this.setVerifyCodeValue(index + offset, value);
      });

      const nextIndex = Math.min(
        index + values.length,
        this.verifyCodes.length - 1
      );
      this.focusVerifyInput(nextIndex);
    },
    handleVerifyKeydown(index, event) {
      if (this.isVerifyLocked) {
        event.preventDefault();
        return;
      }
      if (event.key === "Backspace" && !this.verifyCodes[index] && index > 0) {
        this.focusVerifyInput(index - 1);
      }

      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.focusVerifyInput(index - 1);
      }

      if (event.key === "ArrowRight" && index < this.verifyCodes.length - 1) {
        event.preventDefault();
        this.focusVerifyInput(index + 1);
      }
    },
    handleVerifyPaste(index, event) {
      if (this.isVerifyLocked) return;
      event.preventDefault();
      const pastedText = event.clipboardData.getData("text") || "";
      const digits = pastedText.replace(/\D/g, "");

      if (!digits) {
        return;
      }

      digits
        .slice(0, this.verifyCodes.length - index)
        .split("")
        .forEach((value, offset) => {
          this.setVerifyCodeValue(index + offset, value);
        });

      const nextIndex = Math.min(
        index + digits.length,
        this.verifyCodes.length - 1
      );
      this.focusVerifyInput(nextIndex);
    },
    startVerifyLock(lockUntil) {
      this.lockUntil = lockUntil;
      this.verifyCodes = Array(6).fill("");
      this.updateLockCounter();
      if (this.lockInterval) {
        clearInterval(this.lockInterval);
      }
      if (!this.isVerifyLocked) return;
      this.lockInterval = setInterval(() => {
        this.updateLockCounter();
        if (!this.isVerifyLocked) {
          clearInterval(this.lockInterval);
          this.lockInterval = null;
          this.lockUntil = null;
          this.focusVerifyInput(0);
        }
      }, 1000);
    },
    updateLockCounter() {
      const lockUntilTime = new Date(this.lockUntil).getTime();
      if (Number.isNaN(lockUntilTime)) {
        this.lockCounter = 0;
        return;
      }
      const remainingMilliseconds = lockUntilTime - Date.now();
      this.lockCounter = Math.max(Math.ceil(remainingMilliseconds / 1000), 0);
    },
    runCounter() {
      this.counter = 30;
      const interval = setInterval(() => {
        this.counter--;
        if (this.counter === 0) {
          clearInterval(interval);
        }
      }, 1000);
    },
    retrieveAccount() {
      if (this.isVerifyLocked) return;
      this.isLockEnter = false;
      return this.$api.users
        .forgetPasswordSendCode({ Account: this.account })
        .then((data) => {
          if (!data.IsSuccess) {
            const lockUntil = data?.LockUntil;
            if (lockUntil) {
              this.startVerifyLock(lockUntil);
            }
            this.$func.message({
              type: "error",
              mainMessage: data.Message,
            });
          } else {
            this.$func.message({
              type: "success",
              mainMessage: data.Message,
            });
            this.verifyCodes = Array(6).fill("");
            this.runCounter();
            this.focusVerifyInput(0);
            this.step = 2;
          }
        })
        .catch((error) => {
          this.$func.message({
            type: "error",
            mainMessage: error.data.Message,
          });
        });
    },
    forgetPassword() {
      this.step = 1;
      this.isLogin = false;
    },
    clearExpiredLogin() {
      this.$api.userAccounts.clearLogin();
    },
    finishForcedPasswordChange() {
      this.isForcedPasswordChangeActive = false;
      window.removeEventListener("pagehide", this.clearExpiredLogin);
      window.removeEventListener("beforeunload", this.clearExpiredLogin);
    },
    resetExpiredPasswordInputs() {
      this.expiredPassword = {
        OldPassword: "",
        NewPassword: "",
        ConfirmPassword: "",
      };
    },
    async handleExpiredPassword() {
      this.isForcedPasswordChangeActive = true;
      window.addEventListener("pagehide", this.clearExpiredLogin);
      window.addEventListener("beforeunload", this.clearExpiredLogin);

      await this.$func.message({
        type: "alert",
        mainMessage: "您的密碼已超過90天未修改\n為確保帳號安全，請設定新密碼",
        confirmOnly: true,
        confirmText: "前往修改",
      });

      this.resetExpiredPasswordInputs();
      this.forcePasswordModalTrigger = true;
    },
    cancelExpiredPasswordChange() {
      this.forcePasswordModalTrigger = false;
      this.resetExpiredPasswordInputs();
    },
    saveExpiredPassword() {
      if (
        this.expiredPassword.OldPassword === this.expiredPassword.NewPassword
      ) {
        this.$func.message({
          type: "alert",
          mainMessage: "新密碼不可與舊密碼相同 !",
        });
        return;
      }

      if (
        this.expiredPassword.NewPassword !==
        this.expiredPassword.ConfirmPassword
      ) {
        this.$func.message({
          type: "alert",
          mainMessage: "請輸入相同密碼 !",
        });
        return;
      }

      const passwordData = {
        OldPassword: this.$func.crypto.encrypt(
          this.expiredPassword.OldPassword
        ),
        NewPassword: this.$func.crypto.encrypt(
          this.expiredPassword.NewPassword
        ),
      };

      this.$api.users
        .changePassword(passwordData)
        .then(async () => {
          this.forcePasswordModalTrigger = false;
          this.finishForcedPasswordChange();
          this.clearExpiredLogin();
          this.account = "";
          this.password = "";
          this.isPasswordVisible = false;
          this.resetExpiredPasswordInputs();
          await this.$func.message({
            type: "success",
            mainMessage: "密碼修改成功，請重新登入",
          });
        })
        .catch((error) => {
          this.$func.message({
            type: "error",
            mainMessage: error.data?.Message || "密碼修改失敗 !",
          });
        });
    },
    login() {
      const password = this.$func.crypto.encrypt(this.password);
      const vm = this;
      this.$api.userAccounts.login(vm.account, password).then((data) => {
        if (data.IsPasswordExpired) {
          vm.handleExpiredPassword();
          return;
        }
        vm.finishForcedPasswordChange();
        vm.$router.push({ name: "Home" });
      });
    },
  },
  mounted() {
    const loadingStore = useLoadingStore();
    loadingStore.reset();
    const loginErrorMessage = sessionStorage.getItem("loginErrorMessage");
    if (loginErrorMessage) {
      sessionStorage.removeItem("loginErrorMessage");
      this.$func.message({
        type: "error",
        mainMessage: loginErrorMessage,
        allowDuringUnauthorizedRedirect: true,
      });
    }
    const preloadImages = [this.ushowBanner, this.ushowLogo, this.ushowSlogan];
    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  },
  beforeUnmount() {
    if (this.lockInterval) {
      clearInterval(this.lockInterval);
    }
    if (this.isForcedPasswordChangeActive) {
      this.clearExpiredLogin();
    }
    this.finishForcedPasswordChange();
  },
};
</script>

<style lang="scss">
.login-background {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background-color: #c8dceb;
}
.login-main-block {
  display: flex;
  justify-content: center;
  // align-items: center;
  align-items: stretch;
  width: 80%;
  max-width: 900px;
  padding: 10px;
  border-radius: 16px;
  background-color: white;
  box-shadow: 0px 2px 10px 0 rgba(0, 0, 0, 0.2);
  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
}
.login-news {
  cursor: pointer;
  padding: 10px;
  min-width: 50%;
  @media screen and (max-width: 767px) {
    display: none;
    min-width: none;
  }
  img {
    width: 100%;
  }
}
.login-interface {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  flex-grow: 1;
  @media screen and (max-width: 767px) {
    width: 100%;
  }
}
.login-top-and-middle {
  width: 100%;
}
.login-top {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 80px 50px 10px;
}
.step-info {
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  width: 100%;
}
.login-middle {
  padding: 10px 50px;
  width: 100%;
  @media screen and (max-width: 767px) {
    padding: 10px 20px;
  }
}
.login-bottom {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 50px 50px 10px;
  color: #727272;
  font-size: 14px;
}
.login-img {
  padding: 5px 0;
}
.login-input {
  input {
    width: 100%;
    height: 40px;
    padding: 0 15px;
    border-radius: 4px;
    border: 1px solid #d1d1d1;
  }
}
.password-input-wrapper {
  position: relative;

  .password-input {
    padding-right: 48px;
  }
}
.password-toggle-button {
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  transform: translateY(-50%);

  img {
    width: 20px;
    height: 20px;
    display: block;
  }
}
.account-title,
.password-title {
  margin-top: 10px;
  padding: 10px 0 5px;
}
.login-button {
  button {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 40px;
    margin-top: 30px;
    border: none;
    border-radius: 4px;
    color: white;
    background-color: #4695c9;
    font-size: 16px;
    transition: all 0.3s;
    &:hover {
      filter: brightness(1.1);
    }
    &:disabled {
      cursor: not-allowed;
      background-color: #9fb8c9;
      filter: none;
    }
  }
}
.forget-password,
.resend-request {
  cursor: pointer;
  text-decoration: none;
  color: #4695c9;
  &:hover {
    filter: brightness(1.1);
  }
}
.verify-inputs {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.verify-input {
  text-align: center;
  width: 45px;
  height: 45px;
  border: 1px solid #d1d1d1;
  border-radius: 4px;
  font-size: 20px;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: #4695c9;
    box-shadow: 0 0 0 3px rgba(70, 149, 201, 0.15);
  }
  &:disabled {
    cursor: not-allowed;
    background-color: #f1f1f1;
    color: #999;
  }
}
.verify-lock-message {
  margin-top: 12px;
  color: #c94f46;
  text-align: center;
}
.resend-verify-code,
.lock-until-counter {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  margin-top: 10px;
}
.confirm-message {
  color: #727272;
}
.resend-block {
  margin-top: 2px;
  margin-left: 10px;
}
.resend-counter {
  width: 64px;
  padding: 6px 10px 4px;
  border: 1px solid #d1d1d1;
  border-radius: 4px;
  color: #727272;
}
.main-text {
  font-size: 20px;
  font-weight: 600;
  margin-top: 6px;
  color: #2b2b2b;
}
.sub-text {
  font-size: 18px;
  margin-top: 10px;
  color: #727272;
}
.res-email {
  margin-top: 6px;
}
.back-to-login {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 10px;
  color: #4695c9;
  &:hover {
    filter: brightness(1.1);
  }
}
</style>
